import React, { useState } from "react";
import Slider from "react-slick";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
// NOTE: Please ensure these paths are correct in your project
import img1 from "../assests/testimonalImg/Aman.png"; 
import img2 from "../assests/testimonalImg/Kanti.jpeg"; 
import img3 from "../assests/testimonalImg/Ramesh.webp"; 
import img4 from "../assests/testimonalImg/Ritu.jpg"; 
import img5 from "../assests/testimonalImg/Swati.jpeg"; 

interface Testimonial {
  name: string;
  category: string;
  photoUrl: string;
  feedback: string;
  rating: number;
}

const testimonials: Testimonial[] = [
    { name: "Aman Rajput", category: "Ring Purchase", photoUrl: img1, rating: 5, 
      feedback: "The quality of the ring is exceptional, and the service was outstanding. I highly recommend Sardar Jewellers for their unique and timeless designs.", },
    { name: "Kalyani Singh", category: "Gold Chain", photoUrl: img2, rating: 4, 
      feedback: "My gold chain arrived quickly and was exactly as described. The online experience was smooth, and the craftsmanship is truly beautiful.", },
    { name: "Ramesh Jha", category: "Diamond Necklace", photoUrl: img3, rating: 5, 
      feedback: "I am absolutely thrilled with my diamond necklace. It sparkles wonderfully and has become my favorite piece of jewelry. Truly worth the investment!", },
    { name: "Ritu Sharma", category: "Gold Bangles", photoUrl: img4, rating: 5, 
      feedback: "The gold bangles for the wedding were delivered perfectly. They look even more stunning in person than they did online. A highly reliable service.", },
    { name: "Swati Devi", category: "Wedding Jewelry", photoUrl: img5, rating: 4, 
      feedback: "Sardar Jewellers provided the perfect wedding collection. Excellent variety and professional guidance made the whole process stress-free.", },
];

// Star Rating Component (For Gold color accent)
const RatingStars: React.FC<{ count: number }> = ({ count }) => {
    return (
        <div className="flex justify-start">
            {[...Array(5)].map((_, i) => (
                <Star
                    key={i}
                    className={`w-4 h-4 ${i < count ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} // Amber/Gold Accent
                />
            ))}
        </div>
    );
};

// Custom Arrow Components (Cleaned up and styled for white background)
const NextArrow = (props: any) => {
    const { onClick } = props;
    return (
        <div className="absolute right-[-40px] top-[50%] -translate-y-1/2 z-10 cursor-pointer p-3 rounded-full bg-white shadow-lg hover:bg-amber-100 transition duration-300" onClick={onClick}>
            <ChevronRight className="w-5 h-5 text-amber-700" />
        </div>
    );
};

const PrevArrow = (props: any) => {
    const { onClick } = props;
    return (
        <div className="absolute left-[-40px] top-[50%] -translate-y-1/2 z-10 cursor-pointer p-3 rounded-full bg-white shadow-lg hover:bg-amber-100 transition duration-300" onClick={onClick}>
            <ChevronLeft className="w-5 h-5 text-amber-700" />
        </div>
    );
};

const TestimonialPage = () => {
  const [activeSlide, setActiveSlide] = useState(0); 

  const settings = {
    dots: false, // Hiding dots to keep it clean
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 5000,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    
    // Enabling Center Mode for the curve/zoom effect
    centerMode: true, 
    centerPadding: '0px', 
    beforeChange: (current: number, next: number) => setActiveSlide(next), 
    
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          centerPadding: '100px', 
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          centerPadding: '40px',
        }
      }
    ]
  };

  return (
    <section className="bg-white py-20 px-4 md:px-12">
      <h2 className="text-4xl font-extrabold text-center text-amber-800 mb-16 font-[Cinzel] relative">
        <span className="relative z-10">Our Valued Customers</span>
        <span className="absolute left-1/2 top-full -translate-x-1/2 h-1 w-20 bg-amber-500 mt-2"></span> {/* Underline */}
      </h2>
      
      <div className="max-w-7xl mx-auto relative px-10 testimonial-slider-container">
        <Slider {...settings}>
          {testimonials.map((testimonial, index) => {
            // Logic to determine if the card is the center slide
            const isActive = index === activeSlide; 

            return (
              <div 
                key={index} 
                className="px-4 py-2"
                style={{ 
                    // Dynamic transform for the 3D/Curve effect
                    transition: 'transform 500ms ease-in-out, opacity 500ms ease-in-out',
                    transform: isActive ? 'scale(1.1)' : 'scale(0.95)', // Center=Big, Side=Small
                    opacity: isActive ? 1 : 0.7, // Center=Full Highlight, Side=Subtle
                    zIndex: isActive ? 10 : 1, // Ensure center is on top
                }}
              >
                
                {/* 🛑 NEW MODERN MINIMALIST CARD */}
                <div className="p-8 h-full min-h-[340px] bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col justify-between transition-all duration-300 hover:shadow-2xl">
                  
                  {/* Quote Icon (Amber Accent) */}
                  <div className="text-amber-500 mb-4">
                     {/* Using the quote icon from lucide-react (assuming available or replace with text) */}
                     <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-quote"><path d="M10 11H7a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h3a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H5"/><path d="M20 11h-3a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h3a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-5"/></svg>
                  </div>
                  
                  {/* Feedback Text */}
                  <p className="text-gray-700 text-base leading-relaxed mb-6 flex-grow">
                    {testimonial.feedback}
                  </p>
                  
                  {/* Footer/Customer Info */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center">
                      <img
                        src={testimonial.photoUrl}
                        alt={testimonial.name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-amber-400 mr-3 flex-shrink-0"
                      />
                      <div>
                        <h3 className="text-md font-semibold text-gray-800">
                          {testimonial.name}
                        </h3>
                        <p className="text-xs text-amber-600 font-medium mb-1">{testimonial.category}</p>
                        <RatingStars count={testimonial.rating} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    </section>
  );
};

export default TestimonialPage;